/* =============================================================================
 *  GodWall — the Andrei-curated command WALL (a weight-sized bento mosaic).
 *
 *  Same curated/scored set as the Feed (useFeedModel), rendered spatially: SIZE
 *  encodes importance. The hero tile is the single most important thing; the rest
 *  fan out by rank. There is NO cap — the wall renders one tile per item and
 *  expands as far as the work requires (the owner asked for this explicitly:
 *  nothing that needs attention should be hidden behind a "+N more" summary).
 *
 *  Each tile is PROGRAMMATIC-FIRST: the main area opens the native record (job
 *  workspace, invoice/bill view, email panel) via useFeedActions; jobs carry an
 *  inline status dropdown (direct Firestore write); Andrei is the small "✦"
 *  secondary. The tile = a main <button> (open) + sibling controls (status / ✦),
 *  so there are no nested interactive elements.
 * ============================================================================= */

import React, { useState } from 'react';
import { useFeedModel, Glyph, fmtMoney, greeting, todayLabel, emailKey } from './feedModel';
import { useFeedActions, JobStatusSelect } from './feedActions';
import { useEmailBodies } from './useEmailBodies';
import { useYesterdayAdsKpi } from './useYesterdayAdsKpi';
import WallOptionsModal from './WallOptionsModal';
import GraphiteClock from './GraphiteClock';
import { useAuth } from '../../modules/auth/core/Auth';
import './GodWall.scss';

const sizeForRank = (i: number): 'hero' | 'wide' | 'tall' | 'small' =>
  i === 0 ? 'hero' : i <= 2 ? 'wide' : i <= 4 ? 'tall' : 'small';

const GodWall: React.FC = () => {
  const { items, vitals } = useFeedModel();
  const actions = useFeedActions();
  const ads = useYesterdayAdsKpi();
  const { currentUser } = useAuth();
  // Tolerate snake_case (auth model) AND camelCase (the profile form stores
  // firstName/name) so the greeting reliably finds the user's name.
  const u = currentUser as any;
  const firstName = (u?.first_name || u?.firstName || u?.fullname?.split(' ')?.[0] || u?.name?.split(' ')?.[0] || u?.username || '').trim();
  const [showOptions, setShowOptions] = useState(false);
  // No cap — every ranked item gets its own tile; the wall expands as needed.
  const shown = items;

  // Only the roomy tiles (hero/tall) render an email preview, so only fetch the
  // full body for those — fills the big cards with the real email (the list
  // snapshot only carries a short bit). Falls back to the snippet on miss.
  const previewIds: string[] = [];
  shown.forEach((it, i) => {
    const size = sizeForRank(i);
    if (it.openKind === 'email' && (size === 'hero' || size === 'tall')) {
      const k = emailKey(it.payload?.email);
      if (k) previewIds.push(k);
    }
  });
  const bodies = useEmailBodies(previewIds);

  return (
    <div className="gwall">

      {/* ── Briefing ───────────────────────────────────────────────────────── */}
      <header className="gwall__brief">
        <div>
          <h1 className="gwall__greeting">{greeting()}{firstName ? `, ${firstName}` : ''}</h1>
          {items.length === 0 && (
            <p className="gwall__sub">All clear. Nothing needs you right now. ✦</p>
          )}
        </div>
        <div className="gwall__brief-side">
          <GraphiteClock />
          <span className="gwall__date">{todayLabel()}</span>
          <button className="gwall__ask" onClick={() => actions.ask('Give me a full briefing on what needs my attention today across client emails, jobs, money and ads.')}>
            Ask Andrei
          </button>
          <button className="gwall__opts" title="Wall options" aria-label="Wall options" onClick={() => setShowOptions(true)}>⋯</button>
        </div>
      </header>

      {/* ── Pinned vitals ribbon ───────────────────────────────────────────── */}
      <section className="gwall__vitals">
        <div className="gvital gvital--ads">
          <div className="gvital__ads-head">
            <span className="gvital__ads-title">Google Ads</span>
            <span className="gvital__ads-day">{ads.kpi?.accountName && ads.kpi.accountName !== 'Google Ads' ? `${ads.kpi.accountName} · yesterday` : 'yesterday'}</span>
          </div>
          {ads.loading ? (
            <div className="gvital__ads-empty">Loading…</div>
          ) : ads.kpi ? (
            <>
              <div className="gvital__ads-metrics">
                <div className="gvital__ads-metric">
                  <span className="gvital__ads-num">{fmtMoney(ads.kpi.cost)}</span>
                  <span className="gvital__ads-lbl">spend</span>
                </div>
                <div className="gvital__ads-metric">
                  <span className="gvital__ads-num">{ads.kpi.clicks.toLocaleString()}</span>
                  <span className="gvital__ads-lbl">clicks</span>
                </div>
                <div className="gvital__ads-metric">
                  <span className="gvital__ads-num">{Number.isInteger(ads.kpi.conversions) ? ads.kpi.conversions : ads.kpi.conversions.toFixed(1)}</span>
                  <span className="gvital__ads-lbl">conv.</span>
                </div>
                {ads.kpi.monthSpend > 0 && (
                  <div className="gvital__ads-metric">
                    <span className="gvital__ads-num">{fmtMoney(ads.kpi.monthSpend)}</span>
                    <span className="gvital__ads-lbl">this month</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="gvital__ads-empty">No data yet</div>
          )}
        </div>
        <div className="gvital">
          <div className="gvital__value">{fmtMoney(vitals.payDue)}</div>
          <div className="gvital__label">You owe{vitals.payOverdueCount ? ` · ${vitals.payOverdueCount} overdue` : ''}</div>
        </div>
        <div className="gvital">
          <div className="gvital__value">{fmtMoney(vitals.recvTotal)}</div>
          <div className="gvital__label">Owed to you{vitals.recvOverdueCount ? ` · ${vitals.recvOverdueCount} overdue` : ''}</div>
        </div>
      </section>

      {/* ── The wall ───────────────────────────────────────────────────────── */}
      <section className="gwall__grid">
        {shown.map((it, i) => {
          const size = sizeForRank(i);
          // Big email tiles show the fetched full body when it's arrived, else the snippet.
          const previewText = (it.openKind === 'email' && bodies[emailKey(it.payload?.email)]) || it.preview;
          return (
            <div
              key={it.id}
              className={`gwtile gwtile--${size} gwtile--${it.tone}${it.group === 'decide' ? ' gwtile--decide' : ''}`}
            >
              <button className="gwtile__main" onClick={() => actions.openItem(it)} title="Open">
                <span className="gwtile__top">
                  <span className={`gwtile__icon gwtile__icon--${it.tone}`}><Glyph name={it.glyph} /></span>
                  <span className="gwtile__kicker">{it.groupLabel}</span>
                  {it.amount && <span className="gwtile__amount">{it.amount}</span>}
                  {it.meta && <span className="gwtile__meta">{it.meta}</span>}
                </span>
                <span className="gwtile__title">{it.title}</span>
                <span className="gwtile__sub">{it.subtitle}</span>
                {previewText && <span className="gwtile__preview">{previewText}</span>}
              </button>
              <div className="gwtile__foot">
                {it.jobStatus
                  ? <JobStatusSelect jobId={String(it.payload?.id || '')} status={it.jobStatus} className="gwtile__status" onChange={actions.setJobStatus} />
                  : (it.badge && <span className={`gwtile__badge gwtile__badge--${it.tone}`}>{it.badge}</span>)}
                <span className="gwtile__foot-actions">
                  {it.openKind === 'email' && (
                    <>
                      <button className="gwtile__iconbtn" title="Open & reply" aria-label="Reply" onClick={() => actions.openItem(it)}>
                        <i className="bi bi-reply-fill" />
                      </button>
                      <button className="gwtile__iconbtn" title="Mark read" aria-label="Mark read" onClick={() => actions.markRead(it)}>
                        <i className="bi bi-envelope-open-fill" />
                      </button>
                    </>
                  )}
                  <button className="gwtile__ask" title="Ask Andrei" aria-label="Ask Andrei" onClick={() => actions.askAndrei(it)}>✦</button>
                </span>
              </div>
            </div>
          );
        })}

        {items.length === 0 && (
          <div className="gwall__empty">
            <div className="gwall__empty-mark">✦</div>
            Inbox clear, jobs moving, nothing stalling.
          </div>
        )}
      </section>

      {actions.modals}
      <WallOptionsModal show={showOptions} onClose={() => setShowOptions(false)} />
    </div>
  );
};

export default GodWall;
